import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPageView } from 'goaffpro-client';
import { setCurrentStoryStep } from '../../helper/helper';
import { callAPI } from '../../services/api.service';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { CONFIG } from '../../config/mainConfig';
import { Toast } from '../../components/Common/Toast';
import Heading from '../../components/Common/Heading';
import { PlanDetailsModel } from '../../interfaces/plan.interface';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getPlans } from '../../pages/Admin/Plans/services/Plans.service';
import { runEditStory } from '../../store/storySlice';
import { applyPromoCodeService } from './services/PlanPurchase.service';
import { UserLocalStorageKeysEnum } from '../../interfaces/user.interface';
import { HTTPStatusEnum } from '../../interfaces/log.interface';
import { openPlanPurchaseModal } from '../../store/appSlice';

interface PlanPurchaseProps {
  setShowThanksNote?: (value: boolean) => void;
  setPlanSubscribed?: (value: boolean) => void | null;
  className?: string | null;
}

const PlanPurchase = ({ setShowThanksNote, setPlanSubscribed, className = null }: PlanPurchaseProps) => {
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const bookId = useSelector((state: RootState) => state.app.bookId);
  const bookData = useSelector((state: RootState) => state.app.bookData);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanDescription, setSelectedPlanDescription] = useState<PlanDetailsModel | null>(null);
  const [plans, setPlans] = useState<PlanDetailsModel[]>([]);
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [cardHolderName, setCardHolderName] = useState<string>('');
  const [couponClass, setCouponClass] = useState<string | null>(null);
  const [couponDiscount, setCouponDiscount] = useState<string | null>(null);
  const [couponLoader, setCouponLoader] = useState<boolean>(false);
  const [price, setPrice] = useState<string | null>(null);
  const [placeholder, setPlaceholder] = useState<string>('Coupon (Optional)');
  const [paymentloader, setPaymentLoader] = useState<boolean>(false);
  const [couponDescription, setCouponDescription] = useState<string | null>(null);
  const [trialDays, setTrialDays] = useState<number | null>(null);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    // Get user's country for currency-specific pricing
    const userCountry = user?.shippingCountry || user?.detectedCountry;
    getPlans(setPlans, user?.plan?.name, true, userCountry);
    trackPageView();
  }, [user]);

  useEffect(() => {
    // Get user's country for currency-specific pricing
    const userCountry = user?.shippingCountry || user?.detectedCountry;
    getPlans(setPlans, user?.plan?.name, true, userCountry);
  }, [user?.plan, user]);

  useEffect(() => {
    const selectedObj: any = plans?.find((value: PlanDetailsModel) => value.id === selectedPlan);
    setSelectedPlanDescription(selectedObj);
    setPrice(selectedObj?.cost);
  }, [selectedPlan, plans]);

  const handlePlanChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedObj: any = plans.find((value: PlanDetailsModel) => value.id === event.target.value);
    setSelectedPlanDescription(selectedObj);
    setSelectedPlan(event.target.value);
    setTrialDays(selectedObj?.trialDays > 0 ? selectedObj?.trialDays : null);
  };

  const handleApplyPromoCode = async (): Promise<void> => {
    await applyPromoCodeService(
      couponCode,
      selectedPlanDescription,
      setCouponClass,
      setCouponDescription,
      setCouponDiscount,
      setPrice,
      setCouponCode,
      setCouponLoader
    );
  };

  const handleSubmit = async (): Promise<void> => {
    if (cardHolderName.length <= 1) {
      Toast('Please enter card holder name', true);
      return;
    }
    if (!stripe || !elements)
      return;
    setPaymentLoader(true);

    const card = elements?.getElement(CardElement);
    const resultStripe = card && await stripe.createPaymentMethod({
      type: 'card',
      card: card,
      billing_details: {
        email: user?.email
      }
    });
    if (resultStripe?.error) {
      setPaymentLoader(false);
      Toast(resultStripe?.error?.message, true);
    } else {
      const userData = {
        email: user?.email,
        plan: selectedPlan,
        paymentMethod: resultStripe?.paymentMethod?.id,
        cardHolderName,
        coupon: couponCode,
        bookId: localStorage.getItem(CONFIG.PROMO_PLAN) ? bookId : null
      };
      const paymentResponse = await callAPI('plans/payment', userData);
      if (paymentResponse?.status === HTTPStatusEnum.OK) {
        if (setPlanSubscribed) setPlanSubscribed(true);
        if (localStorage.getItem(CONFIG.PROMO_PLAN)) {
          localStorage.removeItem(UserLocalStorageKeysEnum.BOOK_ID);
          localStorage.removeItem(CONFIG.PROMO_PLAN);
          setCurrentStoryStep(null);
          Toast('You have received a free promo book we hope you enjoy it.');
          setTimeout(() => {
            dispatch(runEditStory({ book: bookData, navigate: navigateTo }));
          }, 5000);
        } else Toast('Plan Purchased Successfully');
        setShowThanksNote && setShowThanksNote(true);
      } else Toast(paymentResponse?.message || 'Unexpected Error Occurred', true);
      setPaymentLoader(false);
    }
  };

  const getUpgradeButtonText = () => {
    return user?.plan && user?.plan?.name !== CONFIG.PROMO_PLAN && user?.plan?.freePhysicalBookStatus !== true
      ? 'Upgrade Plan'
      : 'Purchase Plan';
  };

  const openPurchasePlanPopup = () => {
    setTimeout(() => {
      dispatch(openPlanPurchaseModal({
        show: true,
        data: {
          title: 'Upgrade Plan!',
          description: 'Order a physical copy of the book you\'re creating, or subscribe to a limited-time free trial to finish your current story and start new ones!'
        }
      }));
    }, 0);
  };

  return (
    <form
      id="form-signup"
      className="w-full flex flex-col gap-2 "
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {user?.plan === null ||
      user?.plan?.name === CONFIG.PROMO_PLAN ||
      user?.plan?.freePhysicalBookStatus !== true ? (
        <div className="flex flex-col gap-2 text-base font-medium text-storyblue">
          <div className="flex justify-start items-start gap-4">
            <FontAwesomeIcon icon={faCheck} className=" text-2xl text-storylightgreen" />
            <h1>Access to "Book Builder" craft personalized children's books for your loved one.</h1>
          </div>
          <div className="flex justify-start items-start gap-4">
            <FontAwesomeIcon icon={faCheck} className=" text-2xl text-storylightgreen" />
            <h1>Unlimited Story Editing.</h1>
          </div>
          <div className="flex justify-start items-start gap-4">
            <FontAwesomeIcon icon={faCheck} className=" text-2xl text-storylightgreen" />
            <h1>Unlimited PDFs Generated.</h1>
          </div>
        </div>
      ) : (
        ''
      )}
      <div className=" flex items-center align-end mt-2">
        <Heading title={getUpgradeButtonText()} />
      </div>
      {/*<div className="w-full flex justify-between mt-3 ">*/}
      {/*  <div className="w-full flex flex-col gap-1">*/}
      {/*    <select*/}
      {/*      required*/}
      {/*      name="plan"*/}
      {/*      id="plan"*/}
      {/*      value={selectedPlan!}*/}
      {/*      className="w-[100%] h-10 rounded-md "*/}
      {/*      onChange={(e) => {*/}
      {/*        handlePlanChange(e);*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      <option value={''}>Select Plan</option>*/}
      {/*      {plans?.map((plan: PlanDetailsModel, index) => (*/}
      {/*        <option key={index} value={plan.id} className="capitalize">*/}
      {/*          {plan.name} - $ {plan.cost} /{' '}*/}
      {/*          <span className="capitalize">*/}
      {/*            {capitalizeFirstCharacter(replaceFromString(plan?.chargeCycle!, '_-'))}*/}
      {/*          </span>*/}
      {/*        </option>*/}
      {/*      ))}*/}
      {/*    </select>*/}
      {/*    {selectedPlanDescription && (*/}
      {/*      <div*/}
      {/*        className="bg-white rounded-md border-2 border-storylightgreen flex flex-col gap-1 justify-around items-start w-full my-2 p-2">*/}
      {/*        {selectedPlanDescription?.freePhysicalBookStatus === true &&*/}
      {/*        selectedPlanDescription?.freePhysicalBookQuantity ? (*/}
      {/*          <h1 className="text-md text-storyblue">*/}
      {/*            <strong> Free Physical Book(s): </strong>*/}
      {/*            {selectedPlanDescription?.freePhysicalBookQuantity}*/}
      {/*          </h1>*/}
      {/*        ) : (*/}
      {/*          <h1 className="text-md text-storyblue">*/}
      {/*            <strong> Monthly Stories: </strong> {selectedPlanDescription?.storyCreationLimit}*/}
      {/*          </h1>*/}
      {/*        )}*/}

      {/*        <h1 className="text-md text-storyblue capitalize">*/}
      {/*          <strong> Cost: </strong> $ {selectedPlanDescription?.cost}*/}
      {/*          {' / '}*/}
      {/*          {selectedPlanDescription?.chargeCycle?.replace('_', ' ')}*/}
      {/*        </h1>*/}

      {/*        <span className="capitalize whitespace-pre-wrap">{selectedPlanDescription?.mics || ''}</span>*/}
      {/*      </div>*/}
      {/*    )}*/}
      {/*  </div>*/}
      {/*</div>*/}
      {/*<div*/}
      {/*  className={`flex ${className ? className : 'justify-between'} gap-2 md:gap-4 mt-3 rounded-md h-10`}*/}
      {/*>*/}
      {/*  <input*/}
      {/*    type="text"*/}
      {/*    name="promoCode"*/}
      {/*    value={couponCode ?? ''}*/}
      {/*    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}*/}
      {/*    placeholder={placeholder}*/}
      {/*    className={`p-2 w-[50%] rounded-md uppercase ${couponClass}`}*/}
      {/*  />*/}
      {/*  <button*/}
      {/*    className={`w-60 flex justify-center items-center gap-1 whitespace-nowrap bg-storygreen text-white font-bold rounded-md text-md pr-1 pl-1 h-10 ${!selectedPlanDescription} ? "opacity-50" : ""`}*/}
      {/*    onClick={() => handleApplyPromoCode()}*/}
      {/*    disabled={!selectedPlanDescription}*/}
      {/*  >*/}
      {/*    Apply Coupon / Gift Card {couponLoader && <Loader loader={couponLoader} />}*/}
      {/*  </button>*/}
      {/*</div>*/}
      {/*{selectedPlanDescription && (*/}
      {/*  <p className="flex flex-wrap pt-2 text-sm text-storygreen">*/}
      {/*    {couponDiscount && (*/}
      {/*      <span className="text-storygreen text-sm w-full sm:w-auto">*/}
      {/*        Discount applied of{' '}*/}
      {/*        <b className="pl-1">*/}
      {/*          ${' '}*/}
      {/*          {parseFloat(couponDiscount) > parseFloat(selectedPlanDescription?.cost)*/}
      {/*            ? selectedPlanDescription?.cost*/}
      {/*            : couponDiscount}*/}
      {/*        </b>*/}
      {/*        .*/}
      {/*      </span>*/}
      {/*    )}*/}
      {/*    {trialDays ? (*/}
      {/*      <span className="w-full sm:w-auto">*/}
      {/*        After&nbsp;<strong>{trialDays} trial days</strong>,{' '}*/}
      {/*      </span>*/}
      {/*    ) : (*/}
      {/*      ''*/}
      {/*    )}*/}
      {/*    <span className="w-full sm:w-auto">*/}
      {/*      You will be charged <b className="pl-1"> ${price}</b>*/}
      {/*    </span>*/}
      {/*  </p>*/}
      {/*)}*/}
      {/*{couponDiscount && couponDescription && couponDescription?.length > 0 && (*/}
      {/*  <div className="flex pt-0">*/}
      {/*    <span className=" text-storygreen text-sm">*/}
      {/*      <strong> Description: </strong> {couponDescription}*/}
      {/*    </span>*/}
      {/*  </div>*/}
      {/*)}*/}

      {/*<div className={`w-full mt-3 rounded-md h-10 `}>*/}
      {/*  <input*/}
      {/*    type="text"*/}
      {/*    required*/}
      {/*    name="billingCardHolderName"*/}
      {/*    value={cardHolderName}*/}
      {/*    onChange={(e) => {*/}
      {/*      setCardHolderName(e.target.value);*/}
      {/*    }}*/}
      {/*    placeholder="Card Holder Name"*/}
      {/*    className={`p-2 w-full rounded-md`}*/}
      {/*  />*/}
      {/*</div>*/}
      {/*<div className="mt-3 bg-white p-2 rounded-md h-10">*/}
      {/*  <CheckoutForm />*/}
      {/*</div>*/}
      {/*<button*/}
      {/*  disabled={!selectedPlanDescription || paymentloader}*/}
      {/*  type="submit"*/}
      {/*  className={`w-[50%] flex justify-center gap-2 ${*/}
      {/*    !selectedPlanDescription ? 'opacity-50' : ''*/}
      {/*  } bg-storyblue text-white font-bold rounded-md text-base h-11 items-center mt-5`}*/}
      {/*  onClick={(e) => {*/}
      {/*    e.preventDefault();*/}
      {/*    handleSubmit();*/}
      {/*  }}*/}
      {/*>*/}
      {/*  Purchase Plan*/}
      {/*  <Loader loader={paymentloader} />*/}
      {/*</button>*/}
      <div className="flex items-center sm:w-[100%] md:w-[50%] pt-3">
        <button
          type="button"
          className="flex justify-center self-end gap-2 bg-storyblue text-white font-bold rounded-2xl px-4 py-2 text-sm text-nowrap items-center"
          onClick={() => openPurchasePlanPopup()}
        >
          {getUpgradeButtonText()}
        </button>
      </div>
    </form>
  );
};
export default PlanPurchase;
